// API Client with Authentication for Main App
import axios, { AxiosError, AxiosInstance, AxiosRequestConfig } from 'axios';
import { apiConfig } from '@/config/api.config';
import { refreshAccessToken } from './authRefresh';
import { getCsrf } from './session';

class ApiClient {
  private client: AxiosInstance;
  private isRefreshing = false;
  private failedQueue: Array<{
    resolve: (value?: any) => void;
    reject: (reason?: any) => void;
  }> = [];

  constructor() {
    this.client = axios.create({
      baseURL: apiConfig.baseURL,
      timeout: apiConfig.timeout,
      headers: apiConfig.headers,
      withCredentials: true
    });

    this.setupInterceptors();
  }

  private setupInterceptors(): void {
    // Request interceptor
    this.client.interceptors.request.use(
      (config) => {
        // P2-8: auth rides the httpOnly session cookie (withCredentials on this
        // client). No Authorization header. On state-changing requests attach the
        // session-bound CSRF token.
        const method = (config.method || 'get').toUpperCase();
        if (['POST', 'PUT', 'PATCH', 'DELETE'].includes(method)) {
          config.headers['X-CSRF-Token'] = getCsrf() || '';
        }

        // Add tenant header if available
        const tenant = localStorage.getItem('tenant_data');
        if (tenant) {
          try {
            const tenantData = JSON.parse(tenant);
            if (tenantData.slug) {
              config.headers['X-Tenant-Slug'] = tenantData.slug;
            }
            if (tenantData.id) {
              config.headers['X-Tenant-Id'] = tenantData.id;
            }
          } catch (e) {
            console.error('Failed to parse tenant data:', e);
          }
        }

        // Add request ID for tracking
        config.headers['X-Request-Id'] = this.generateRequestId();

        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor
    this.client.interceptors.response.use(
      (response) => response,
      async (error: AxiosError) => {
        const originalRequest = error.config as AxiosRequestConfig & { _retry?: boolean };

        // Handle 401 Unauthorized — refresh the access token via the httpOnly
        // refresh cookie (the single source of truth, shared with hrApi), retry
        // once, and only on a failed refresh treat the session as dead.
        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;
          const refreshed = await refreshAccessToken();
          if (refreshed) {
            // Cookie rotated by the backend; re-send with the cookie (no Bearer).
            return this.client(originalRequest);
          }
          // Unrecoverable — go to a clean login (not /auth/login, which 404s).
          const onAuthPage = /\/(login|signin|signup|forgot-password|reset-password|onboarding)/.test(window.location.pathname);
          if (!onAuthPage) window.location.assign('/login?expired=1');
          return Promise.reject(error);
        }

        // Handle other errors
        return this.handleError(error);
      }
    );
  }

  private processQueue(error: any, token: string | null): void {
    this.failedQueue.forEach((prom) => {
      if (error) {
        prom.reject(error);
      } else {
        prom.resolve(token);
      }
    });
    this.failedQueue = [];
  }

  private clearAuth(): void {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user_data');
    localStorage.removeItem('tenant_data');
  }

  private generateRequestId(): string {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  private handleError(error: AxiosError): Promise<never> {
    const errorResponse = error.response?.data as any;
    
    // Create a user-friendly error message
    let message = 'An unexpected error occurred';
    
    if (errorResponse) {
      message = errorResponse.message || errorResponse.error || message;
      
      // Handle validation errors
      if (errorResponse.errors && Array.isArray(errorResponse.errors)) {
        message = errorResponse.errors.map((e: any) => e.message || e).join(', ');
      }
    } else if (error.request) {
      message = 'No response from server. Please check your connection.';
    } else {
      message = error.message;
    }

    // Log error for debugging
    if (apiConfig.isDevelopment) {
      console.error('API Error:', {
        url: error.config?.url,
        method: error.config?.method,
        status: error.response?.status,
        message,
        data: errorResponse
      });
    }

    // Create enhanced error
    const enhancedError = new Error(message) as any;
    enhancedError.status = error.response?.status;
    enhancedError.data = errorResponse;
    enhancedError.originalError = error;

    return Promise.reject(enhancedError);
  }

  // Public API methods
  public get<T = any>(url: string, config?: AxiosRequestConfig) {
    return this.client.get<T>(url, config);
  }

  public post<T = any>(url: string, data?: any, config?: AxiosRequestConfig) {
    return this.client.post<T>(url, data, config);
  }

  public put<T = any>(url: string, data?: any, config?: AxiosRequestConfig) {
    return this.client.put<T>(url, data, config);
  }

  public patch<T = any>(url: string, data?: any, config?: AxiosRequestConfig) {
    return this.client.patch<T>(url, data, config);
  }

  public delete<T = any>(url: string, config?: AxiosRequestConfig) {
    return this.client.delete<T>(url, config);
  }

  // Upload method for files
  public upload<T = any>(url: string, formData: FormData, onProgress?: (progress: number) => void) {
    return this.client.post<T>(url, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      },
      onUploadProgress: (progressEvent) => {
        if (onProgress && progressEvent.total) {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onProgress(progress);
        }
      }
    });
  }
}

// Create and export singleton instance
export const apiClient = new ApiClient();
export default apiClient;